import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import EditorWatchdog from '@ckeditor/ckeditor5-watchdog/src/editorwatchdog'

import fr from 'ckeditor5/translations/fr.js'
import ca from 'ckeditor5/translations/ca.js'
import de from 'ckeditor5/translations/de.js'
import es from 'ckeditor5/translations/es.js'
import it from 'ckeditor5/translations/it.js'
import nl from 'ckeditor5/translations/nl.js'
import pt from 'ckeditor5/translations/pt.js'

import type { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig'
import type EventInfo from '@ckeditor/ckeditor5-utils/src/eventinfo'
import { UpcastWriter } from '@ckeditor/ckeditor5-engine'

import 'ckeditor5/ckeditor5.css'

export type CKEditorConfig = {
  debug?: boolean
  data?: string
  disabled?: boolean
  initialConfig?: EditorConfig
  onBlur?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void
  onChange?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void
  onDestroy?: (editor: ClassicEditor) => void
  onError?: (error: any, meta: { phase: 'initialization' | 'runtime'; willEditorRestart: boolean }) => void
  onFocus?: (event: EventInfo<string, unknown>, editor: ClassicEditor) => void
  onReady?: (editor: ClassicEditor) => void
}

export class CKEditor {
  private watchdog: EditorWatchdog
  private onDestroy?: (editor: ClassicEditor) => void

  static async create(element: HTMLElement, config: CKEditorConfig): Promise<CKEditor> {
    return new CKEditor(await CKEditor.createWatchdog(element, config), config.onDestroy)
  }

  private static async createWatchdog(element: HTMLElement, config: CKEditorConfig): Promise<EditorWatchdog> {
    const watchdog = new EditorWatchdog(ClassicEditor)

    watchdog.setCreator((element, initialConfig) => {
      if (!(element instanceof HTMLElement)) {
        throw new Error('not an htmml element')
      }
      return CKEditor.createEditor(element, { ...config, initialConfig })
    })

    watchdog.on('error', (_, { error, causesRestart }) => {
      console.log(error)
      if (config.onError) {
        config.onError(error, { phase: 'runtime', willEditorRestart: causesRestart })
      }
    })

    try {
      await watchdog.create(element, config.initialConfig)
    } catch (error) {
      console.log(error)
      if (config.onError) {
        config.onError(error, { phase: 'initialization', willEditorRestart: false })
      }
    }

    return watchdog
  }

  private static async createEditor(element: HTMLElement, config: CKEditorConfig): Promise<ClassicEditor> {
    const editor = await ClassicEditor.create(element, {
      ...config.initialConfig,
      licenseKey: 'GPL',
      translations: [fr, ca, de, es, it, nl, pt],
    })

    const writer = new UpcastWriter(editor.editing.view.document)

    editor.plugins.get('ClipboardPipeline').on('inputTransformation', (evt, data) => {
      const imgLessContent = recursivelyRemoveImgChildren(data.content._children)

      data.content = writer.createDocumentFragment(imgLessContent)

      function recursivelyRemoveImgChildren(children: any): any[] {
        return children.filter((child: any) => {
          if (child.name === 'img') {
            return false
          }
          if (child._children) {
            child._children = recursivelyRemoveImgChildren(child._children)
          }
          return true
        })
      }
    })

    editor.model.document.on('change:data', (event) => {
      if (config.onChange) {
        config.onChange(event, editor)
      }
    })

    editor.model.document.on('focus', (event) => {
      if (config.onFocus) {
        config.onFocus(event, editor)
      }
    })

    editor.model.document.on('blur', (event) => {
      if (config.onBlur) {
        config.onBlur(event, editor)
      }
    })

    if (config.onReady) {
      config.onReady(editor)
    }

    if (config.disabled) {
      setDisabled(editor, config.disabled)
    }

    return editor
  }

  private constructor(watchdog: EditorWatchdog, onDestroy?: (editor: ClassicEditor) => void) {
    this.watchdog = watchdog
    this.onDestroy = onDestroy
  }

  getEditor(): ClassicEditor {
    return this.watchdog.editor as ClassicEditor
  }

  setData(data: string): void {
    const editor = this.getEditor()
    editor.setData(data)
  }

  getData(): string {
    const editor = this.getEditor()
    return editor.getData()
  }

  destroy(): void {
    if (this.watchdog.editor === null) {
      console.error('[Wysiwyg] Tried to destroy an editor that was already destroyed')
      return
    }

    if (this.onDestroy) {
      this.onDestroy(this.getEditor())
    }
    this.watchdog.destroy()
  }

  setDisabled(disabled?: boolean): void {
    setDisabled(this.getEditor(), disabled)
  }

  focus(): void {
    this.getEditor().focus()
  }
}

function setDisabled(editor: ClassicEditor, disabled?: boolean): void {
  if (disabled === true) {
    editor.enableReadOnlyMode('CKEditor')
  } else {
    editor.disableReadOnlyMode('CKEditor')
  }
}

// HACK: here be dragons !! CKEditor now adds width and height attributes to
// images when :
// - inserting a new image
// - resizing an existing image
// - copypasta some image link
// - maybe some other operations on the image link switching between
//   inline/block ?? Each of those operation is implemented via a command that
//   call some utils method. This utils method allows disabling the new
//   features, but the commands using those method don't pass the correct
//   parameter. To utilize it, we would have to reimplemente each built in
//   command of CKEditor related to image manipulation, which is a no-no.
//
// The hacky solution is to monkeypatch `editor.getData` that returns the
// serialized html from the editor. In this monkeypatched method we parse the
// html, query all `img` and remove the offending attributes, and serialize the
// document back to html. Really inneficient, bordering on gross, but does the
// job.
const parser = new DOMParser()
const originalGetData = ClassicEditor.prototype.getData
ClassicEditor.prototype.getData = function () {
  const data = originalGetData.call(this)
  if (!data) {
    return data
  }

  const dom = parser.parseFromString(data, 'text/html')

  dom.querySelectorAll('img').forEach((img) => {
    img.removeAttribute('width')
    img.removeAttribute('height')
  })

  return dom.documentElement.innerHTML
}
